// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Spinner } from '@loa/ui-components';
import { policyApi } from '../../../../api/policyApi';
import { policyMetadataApi } from '../../../../api/policyMetadataApi';
import { providerSubmissionApi } from '../../../../api/providerSubmissionApi';
import type { Policy } from '../../../../types/policy';
import type { SelectedPension } from '../../../../types/calculationResult';
import { extractFieldValues, mergeMetadataFields } from '../../../../utils/metadataFieldMerge';
import { classifyPensionPolicy, isPensionPolicy } from './pensionClassification';
import { formatCurrencyEUR } from './maxFundingDefaults';

function extractPolicyValue(fields: Record<string, unknown> | undefined): number | null {
  if (!fields) return null;
  const raw = fields.policyValue;
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

function extractEmployerName(fields: Record<string, unknown> | undefined): string | null {
  if (!fields) return null;
  const raw = fields.employerName;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function hasLeavingServiceDate(fields: Record<string, unknown> | undefined): boolean {
  if (!fields) return false;
  const raw = fields.dateOfLeavingService;
  if (typeof raw !== 'string') return false;
  return raw.trim().length > 0;
}

export const PENSION_TYPE_LABELS: Record<string, string> = {
  PRB_BOB: 'PRB / Buy-out Bond',
  PRSA_STANDARD: 'PRSA (Standard)',
  PRSA_NON_STANDARD: 'PRSA (Non-Standard)',
  PRSA_AVC: 'PRSA AVC',
  PERSONAL_PENSION: 'Personal Pension',
  DEFINED_CONTRIBUTION: 'Defined Contribution',
  EXECUTIVE_PENSION: 'Executive Pension',
  MASTERTRUST: 'Mastertrust',
  DIRECTORS_PENSION: "Director's Pension",
  ARF: 'ARF (Approved Retirement Fund)',
  VESTED_PRSA: 'Vested PRSA',
};

export function extractPolicyAccountNumber(
  fields: Record<string, unknown> | undefined,
): string | null {
  if (!fields) return null;
  const raw = fields.policyAccountNumber;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function extractPensionTypeLabel(
  fields: Record<string, unknown> | undefined,
): string | null {
  if (!fields) return null;
  const raw = fields.pensionType;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  return PENSION_TYPE_LABELS[trimmed] ?? trimmed;
}

type PensionPolicyPickerProps = Readonly<{
  profileId: string;
  clientId: string;
  selectedPensions: SelectedPension[];
  onChange: (next: SelectedPension[]) => void;
  disabled?: boolean;
}>;

export function PensionPolicyPicker({
  profileId,
  clientId,
  selectedPensions,
  onChange,
  disabled,
}: PensionPolicyPickerProps) {
  const [policies, setPolicies] = useState<Policy[] | null>(null);
  const [amountByPolicyId, setAmountByPolicyId] = useState<Record<string, number | null>>({});
  const [employerByPolicyId, setEmployerByPolicyId] = useState<Record<string, string | null>>({});
  const [leftServiceByPolicyId, setLeftServiceByPolicyId] = useState<Record<string, boolean>>({});
  const [policyAccountByPolicyId, setPolicyAccountByPolicyId] = useState<Record<string, string | null>>({});
  const [pensionTypeLabelByPolicyId, setPensionTypeLabelByPolicyId] = useState<Record<string, string | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    policyApi
      .getPoliciesByProfile(profileId)
      .then((all) => {
        if (cancelled) return;
        setPolicies(all);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load policies');
        setPolicies([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  const pensionPolicies = useMemo(() => {
    if (!policies) return [];
    return policies.filter(
      (p) =>
        isPensionPolicy(p) &&
        p.signatories.some(
          (s) =>
            s.clientId === clientId &&
            (s.relationshipType === 'OWNER' ||
              s.relationshipType === 'OWNER_AND_SIGNATORY'),
        ),
    );
  }, [policies, clientId]);

  // Fetch structured metadata + the latest Provider Form submission in parallel
  // for each visible pension policy, then merge them so policies populated only
  // via the Provider Landing Page (no manually-saved metadata yet) still surface
  // their values here. Mirrors the merge done in PolicyMetadataTab.
  useEffect(() => {
    if (pensionPolicies.length === 0) {
      setAmountByPolicyId({});
      setEmployerByPolicyId({});
      setLeftServiceByPolicyId({});
      setPolicyAccountByPolicyId({});
      setPensionTypeLabelByPolicyId({});
      return;
    }
    let cancelled = false;
    Promise.all(
      pensionPolicies.map(async (p) => {
        try {
          const [meta, latestSubmission] = await Promise.all([
            policyMetadataApi.getStructuredDetails(p.id).catch(() => null),
            providerSubmissionApi.getLatestFormSubmission(p.id).catch(() => null),
          ]);
          const structuredFields = (meta?.fields ?? {}) as Record<string, unknown>;
          const providerFormData = (latestSubmission?.formData ?? null) as
            | Record<string, unknown>
            | null;
          const mergedFlat = extractFieldValues(
            mergeMetadataFields(structuredFields, providerFormData),
          );
          return [
            p.id,
            extractPolicyValue(mergedFlat),
            extractEmployerName(mergedFlat),
            hasLeavingServiceDate(mergedFlat),
            extractPolicyAccountNumber(mergedFlat),
            extractPensionTypeLabel(mergedFlat),
          ] as const;
        } catch {
          return [p.id, null, null, false, null, null] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) return;
      const nextAmount: Record<string, number | null> = {};
      const nextEmployer: Record<string, string | null> = {};
      const nextLeftService: Record<string, boolean> = {};
      const nextAccount: Record<string, string | null> = {};
      const nextPensionType: Record<string, string | null> = {};
      for (const [id, amount, employer, leftService, accountNumber, pensionTypeLabel] of entries) {
        nextAmount[id] = amount;
        nextEmployer[id] = employer;
        nextLeftService[id] = leftService;
        nextAccount[id] = accountNumber;
        nextPensionType[id] = pensionTypeLabel;
      }
      setAmountByPolicyId(nextAmount);
      setEmployerByPolicyId(nextEmployer);
      setLeftServiceByPolicyId(nextLeftService);
      setPolicyAccountByPolicyId(nextAccount);
      setPensionTypeLabelByPolicyId(nextPensionType);
    });
    return () => {
      cancelled = true;
    };
  }, [pensionPolicies]);

  const selectedIds = useMemo(
    () => new Set(selectedPensions.map((s) => s.policyId)),
    [selectedPensions],
  );

  const toggleChecked = (policy: Policy) => {
    if (selectedIds.has(policy.id)) {
      onChange(selectedPensions.filter((s) => s.policyId !== policy.id));
    } else {
      onChange([
        ...selectedPensions,
        {
          policyId: policy.id,
          subType: classifyPensionPolicy(policy, pensionTypeLabelByPolicyId[policy.id]),
          amount: amountByPolicyId[policy.id] ?? null,
          leftService: leftServiceByPolicyId[policy.id] ?? false,
          isActive: policy.status === 'active',
        },
      ]);
    }
  };

  // Reconcile amounts and metadata that arrived after a selection was made — if
  // a selected policy's amount / leftService / isActive are now known but the
  // selection still carries stale defaults, patch them upstream so the form's
  // auto-populate effect can pick them up.
  useEffect(() => {
    if (selectedPensions.length === 0) return;
    const policyById = new Map<string, Policy>();
    for (const p of policies ?? []) policyById.set(p.id, p);
    let changed = false;
    const next = selectedPensions.map((sel) => {
      const freshAmount = amountByPolicyId[sel.policyId];
      const p = policyById.get(sel.policyId);
      const freshSubType = p
        ? classifyPensionPolicy(p, pensionTypeLabelByPolicyId[sel.policyId])
        : sel.subType;
      const freshLeftService = leftServiceByPolicyId[sel.policyId];
      const freshIsActive = p ? p.status === 'active' : sel.isActive;
      const needsAmount =
        (sel.amount == null || !Number.isFinite(sel.amount)) && freshAmount != null;
      const needsSubType = !sel.subType && freshSubType;
      const needsLeftService =
        sel.leftService === undefined && freshLeftService !== undefined;
      const needsIsActive = sel.isActive === undefined && freshIsActive !== undefined;
      if (needsAmount || needsSubType || needsLeftService || needsIsActive) {
        changed = true;
        return {
          ...sel,
          amount: needsAmount ? freshAmount : sel.amount,
          subType: sel.subType ?? freshSubType,
          leftService: needsLeftService ? freshLeftService : sel.leftService,
          isActive: needsIsActive ? freshIsActive : sel.isActive,
        };
      }
      return sel;
    });
    if (changed) onChange(next);
  }, [amountByPolicyId, leftServiceByPolicyId, pensionTypeLabelByPolicyId, policies, selectedPensions, onChange]);

  return (
    <div className="flex flex-col gap-sm" data-testid="pension-policy-picker">
      <h4 className="text-body-02 font-medium text-black font-sans m-0">
        Choose existing pensions to populate
      </h4>

      {loading && (
        <div className="flex items-center gap-sm text-body-03 text-grey-04">
          <Spinner size="sm" />
          Loading policies…
        </div>
      )}

      {error && (
        <Alert variant="error" header="Could not load policies">
          <p>{error}</p>
        </Alert>
      )}

      {!loading && !error && pensionPolicies.length === 0 && (
        <p className="text-body-03 text-grey-04 m-0">
          This client has no pension policies registered.
        </p>
      )}

      {!loading && !error && pensionPolicies.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-[#E5E5EA]">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-lilac-05">
                <th className="w-[48px] px-[12px] py-[10px]"></th>
                <th className="text-left px-[12px] py-[10px] text-body-03 font-medium text-black font-sans">
                  Provider
                </th>
                <th className="text-left px-[12px] py-[10px] text-body-03 font-medium text-black font-sans">
                  Policy Type
                </th>
                <th className="text-left px-[12px] py-[10px] text-body-03 font-medium text-black font-sans">
                  Employer
                </th>
                <th className="text-left px-[12px] py-[10px] text-body-03 font-medium text-black font-sans">
                  Policy No.
                </th>
                <th className="text-right px-[12px] py-[10px] text-body-03 font-medium text-black font-sans">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {pensionPolicies.map((policy) => {
                const checked = selectedIds.has(policy.id);
                const amount = amountByPolicyId[policy.id] ?? null;
                const employer = employerByPolicyId[policy.id] ?? null;
                return (
                  <tr
                    key={policy.id}
                    className="border-t border-[#E5E5EA]"
                    data-testid={`pension-row-${policy.id}`}
                  >
                    <td className="px-[12px] py-[10px]">
                      <Checkbox
                        size="sm"
                        checked={checked}
                        onChange={() => toggleChecked(policy)}
                        disabled={disabled}
                        aria-label={`Select ${policy.providerName ?? 'policy'} ${policy.policyNumber || policyAccountByPolicyId[policy.id] || ''}`}
                      />
                    </td>
                    <td className="px-[12px] py-[10px]">
                      <ProviderCell
                        name={policy.providerName ?? '—'}
                        logoUrl={policy.providerLogoUrl}
                      />
                    </td>
                    <td className="px-[12px] py-[10px] text-body-03 text-black font-sans">
                      {policy.policyTypeName || policy.policyType || pensionTypeLabelByPolicyId[policy.id] || '—'}
                    </td>
                    <td
                      className="px-[12px] py-[10px] text-body-03 text-black font-sans"
                      data-testid={`pension-employer-${policy.id}`}
                    >
                      {employer ?? '—'}
                    </td>
                    <td className="px-[12px] py-[10px] text-body-03 text-black font-sans">
                      {policy.policyNumber || policyAccountByPolicyId[policy.id] || '—'}
                    </td>
                    <td
                      className="px-[12px] py-[10px] text-right text-body-03 text-black font-sans"
                      data-testid={`pension-amount-${policy.id}`}
                    >
                      {formatCurrencyEUR(amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProviderCell({ name, logoUrl }: Readonly<{ name: string; logoUrl?: string }>) {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <div className="flex items-center gap-sm">
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="w-[24px] h-[24px] rounded-full object-cover bg-white border border-[#E5E5EA]"
        />
      ) : (
        <span className="w-[24px] h-[24px] rounded-full bg-lilac-05 text-purple-primary text-body-04 font-medium flex items-center justify-center">
          {initial}
        </span>
      )}
      <span className="text-body-03 text-black font-sans">{name}</span>
    </div>
  );
}
